import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ShieldCheck, MessageCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Switch } from '@/components/ui/switch';

export function ActivityTracking() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me/activity-tracking').then(r => setEnabled(r.data.enabled)).catch(() => setEnabled(true));
  }, []);

  const toggle = async (value) => {
    setSaving(true);
    try {
      await api.put('/auth/me/activity-tracking', { enabled: value });
      setEnabled(value);
      toast.success(value ? 'Registo de atividade reativado.' : 'Deixámos de registar a tua atividade diária e apagámos o histórico.');
    } catch {
      toast.error('Erro ao guardar a preferência.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 md:p-8" data-testid="activity-tracking">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground">Registo de atividade diária</h2>
          <p className="text-sm text-muted-foreground mt-1">Guardamos só o dia em que a tua conta esteve ativa (nunca o que leste), para estatísticas agregadas. Apagado ao fim de 90 dias.</p>
          <p className="text-xs text-muted-foreground mt-2">Ao desligar, deixamos de registar e apagamos o histórico existente.</p>
        </div>
        <Switch checked={!!enabled} disabled={enabled === null || saving} onCheckedChange={toggle} data-testid="activity-tracking-switch" />
      </div>
    </div>
  );
}

export function InternalMessagesPref() {
  const { refreshIm } = useAuth();
  const [granted, setGranted] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/im/consent').then(r => setGranted(!!r.data.granted)).catch(() => setGranted(false));
  }, []);

  const toggle = async (value) => {
    setSaving(true);
    try {
      await api.post('/im/consent', { granted: value, source: 'account_settings' });
      setGranted(value);
      refreshIm();
      toast.success(value ? 'Mensagens internas ativadas.' : 'Deixas de receber mensagens promocionais na Intimate.pt.');
    } catch {
      toast.error('Erro ao guardar a preferência.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 md:p-8" data-testid="internal-messages-pref">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-ruby" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground">Mensagens internas</h2>
          <p className="text-sm text-muted-foreground mt-1">Receber novidades, novos conteúdos e campanhas diretamente na Intimate.pt.</p>
          <p className="text-xs text-muted-foreground mt-2">
            Avisos essenciais sobre a tua conta continuam a chegar. <Link to="/mensagens" className="text-ruby underline" data-testid="pref-open-messages">Ver mensagens</Link>
          </p>
        </div>
        <Switch checked={!!granted} disabled={granted === null || saving} onCheckedChange={toggle} data-testid="internal-messages-switch" />
      </div>
    </div>
  );
}

export function EmailPreferences() {
  const [consent, setConsent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me/marketing-consent').then(r => setConsent(r.data)).catch(() => setConsent({ granted: false }));
  }, []);

  const toggle = async (granted) => {
    setSaving(true);
    try {
      const { data } = await api.put('/auth/me/marketing-consent', { granted });
      setConsent(data);
      toast.success(granted ? 'Vais receber as novidades da Intimate.pt.' : 'Deixaste de receber novidades e campanhas.');
    } catch {
      toast.error('Erro ao guardar a preferência.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 md:p-8" data-testid="email-preferences">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5 text-ruby" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground">Novidades e campanhas promocionais</h2>
          <p className="text-sm text-muted-foreground mt-1">Novidades sobre contos, séries, conteúdos exclusivos e campanhas. Podes cancelar a qualquer momento.</p>
          <p className="text-xs text-muted-foreground mt-2">Os e-mails essenciais da conta (verificação, palavra-passe, subscrição) são sempre enviados.</p>
        </div>
        <Switch
          checked={!!consent?.granted}
          disabled={consent === null || saving}
          onCheckedChange={toggle}
          data-testid="marketing-consent-switch"
        />
      </div>
    </div>
  );
}
